/**
 * /register-portal — creating an Admin Portal. Name, email, phone,
 * organisation, address, description: EVERY portal opens instantly on the
 * 15-day free trial, with no Super Admin review (owner's direction,
 * 6 Sep 2026). The reviewed "submit a paid plan for approval" form that used
 * to sit here is retired — its API route survives for records in flight.
 *
 * It also asks WHICH PLAN (owner's direction, 13 Sep 2026). Some clubs arrive
 * having already decided, and a form that only offers a trial makes them go
 * looking for the way to pay. Choosing a paid plan here changes nothing about
 * what is created — the portal still opens on the trial, because capacity
 * follows verified money and nothing else — but the payment request is raised
 * with it, so the Subscription tab is a pay desk with the club's amount on it
 * the first time they open it. The free days are not spent by paying early:
 * the paid year is counted from the END of the trial.
 *
 * An email that already has an account attaches to it (FR-PS-04) — the
 * server handles that; the form does not ask.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { platform } from '../lib/api';
import { formatLongDate, formatMoney } from '../data';
import { Brand } from './SignIn';
import { Field, FormError, INPUT_CLS, PhoneInput, Row2, TEXTAREA_CLS } from '../components/ui';

const TRIAL_FACTS = [
  ['15', 'days free'],
  ['30', 'member seats'],
  ['20', 'events'],
];

export function RegisterPortalPage() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    portalName: '',
    address: '',
    description: '',
  });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  /** The server's answer: the trial's dates and whether an invite went out. */
  const [receipt, setReceipt] = useState(null);
  /* '' is the free trial — the default, and what most people take. A plan id
     here means "I am ready to pay", and rides along with the signup. */
  const [planId, setPlanId] = useState('');
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    let alive = true;
    platform
      .plans()
      .then((r) => alive && setPlans((r.plans ?? []).filter((p) => !p.isTrial)))
      .catch(() => {}) // no ladder, no picker — the trial still works
      .finally(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const chosen = plans.find((p) => p.id === planId) ?? null;

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      setReceipt(await platform.startTrial({ ...form, planId: planId || undefined }));
      window.scrollTo(0, 0);
    } catch (err) {
      setError(err?.message ?? 'Could not create your portal. Try again.');
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-ground">
      <header className="bg-[linear-gradient(168deg,#161C2C_0%,#0D1220_52%,#0A0E19_100%)] px-5 py-4 text-white">
        <div className="mx-auto flex max-w-xl items-center justify-between">
          <Brand dark />
          <Link to="/" className="font-market text-xs uppercase tracking-wide text-white/60 hover:text-white">
            ← Back
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-xl px-5 py-8">
        {receipt ? (
          <TrialAcknowledgement receipt={receipt} request={form} />
        ) : (
          <form onSubmit={submit} className="border border-line bg-white p-6">
            <h1 className="font-market text-2xl tracking-[-0.03em] font-bold text-ink-700">
              Create your Admin Portal
            </h1>
            <p className="mt-1 text-sm text-copy-muted">
              One organisation, unlimited clubs. It opens instantly on the free
              trial — no review, nothing to pay. Already know your plan? Pick it
              below and we will have the bill waiting inside.
            </p>

            <div className="mt-4 grid grid-cols-3 border border-line">
              {TRIAL_FACTS.map(([n, label], i) => (
                <div key={label} className={`px-3 py-3 text-center ${i > 0 ? 'border-l border-line' : ''}`}>
                  <p className="font-market text-[20px] font-extrabold tabular-nums text-ink-700">{n}</p>
                  <p className="font-market text-[9.5px] font-bold uppercase tracking-[0.16em] text-copy-label">
                    {label}
                  </p>
                </div>
              ))}
            </div>

            {/* The ladder. The trial is first and selected: it is what most
                people want, and a club that has already decided should not
                have to hunt for the way to pay. */}
            {plans.length > 0 && (
              <div className="mt-5">
                <p className="font-market text-[10px] font-bold uppercase tracking-[0.16em] text-copy-label">
                  Plan
                </p>
                <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <PlanChoice
                    title="Free trial"
                    line="15 days · ₹0"
                    selected={planId === ''}
                    onPick={() => setPlanId('')}
                  />
                  {plans.map((p) => (
                    <PlanChoice
                      key={p.id}
                      title={p.name}
                      line={`${p.seats} seats · ${formatMoney(p.price)}`}
                      selected={planId === p.id}
                      onPick={() => setPlanId(p.id)}
                    />
                  ))}
                </div>
                <p className="mt-2 font-market text-[11.5px] leading-[1.6] text-copy-label">
                  {chosen ? (
                    <>
                      Your portal opens now on the free trial, with{' '}
                      <b className="text-copy">{formatMoney(chosen.price)}</b> waiting on the
                      Subscription tab. Pay whenever you like — your paid year is counted from
                      the END of the 15 free days, so nothing is lost by paying early.
                    </>
                  ) : (
                    'Nothing to pay, nothing to cancel. You can pick a paid plan from inside at any time.'
                  )}
                </p>
              </div>
            )}

            <div className="mt-5 space-y-4">
              <Row2>
                <Field label="Your name" required>
                  <input className={INPUT_CLS} value={form.name} onChange={set('name')} />
                </Field>
                <Field label="Mobile number" required>
                  <PhoneInput
                    value={form.phone}
                    onChange={(v) => setForm((f) => ({ ...f, phone: v }))}
                  />
                </Field>
              </Row2>
              <Field label="Email address" required>
                <input
                  type="email"
                  className={INPUT_CLS}
                  value={form.email}
                  onChange={set('email')}
                  placeholder="This becomes your sign-in"
                />
              </Field>
              <Field label="Organisation name" required>
                <input
                  className={INPUT_CLS}
                  value={form.portalName}
                  onChange={set('portalName')}
                  placeholder="e.g. Coimbatore Track Club"
                />
              </Field>
              <Field label="Address">
                <input className={INPUT_CLS} value={form.address} onChange={set('address')} />
              </Field>
              <Field label="About the organisation">
                <textarea
                  className={TEXTAREA_CLS}
                  value={form.description}
                  onChange={set('description')}
                  placeholder="How many runners, how many groups, where you run…"
                />
              </Field>
            </div>

            {error && <FormError>{error}</FormError>}

            <button
              type="submit"
              disabled={busy}
              className="mt-6 w-full bg-gold-500 py-3 font-market text-sm font-semibold uppercase tracking-wide text-ink-800 disabled:opacity-60"
            >
              {busy
                ? 'Creating your portal…'
                : chosen
                  ? `Create my portal · ${chosen.name}`
                  : 'Start my free trial'}
            </button>
            <p className="mt-3 text-center text-xs text-copy-label">
              Already have an account?{' '}
              <Link to="/signin" className="text-gold-800 underline">
                Sign in
              </Link>{' '}
              — an existing email attaches to its account automatically.
            </p>
          </form>
        )}
      </main>
    </div>
  );
}

/** One plan in the signup ladder. A radio in everything but markup. */
function PlanChoice({ title, line, selected, onPick }) {
  return (
    <button
      type="button"
      onClick={onPick}
      aria-pressed={selected}
      className={`border px-3 py-2.5 text-left transition-colors duration-200 ${
        selected ? 'border-gold-500 bg-gold-50' : 'border-line hover:border-ink-700'
      }`}
    >
      <span className="block font-market text-[12.5px] font-extrabold tracking-[-0.01em] text-ink-700">
        {title}
      </span>
      <span className="mt-0.5 block font-market text-[10.5px] tabular-nums text-copy-muted">
        {line}
      </span>
    </button>
  );
}

/* --------------------------------------------------- trial acknowledgement */

/**
 * The trial's version of the receipt. There is no queue and no callback —
 * the portal already exists — so the job here is different: say what just
 * happened, say what the trial holds, and put the ONE next action (the
 * password email, or sign-in) where it cannot be missed.
 */
function TrialAcknowledgement({ receipt, request }) {
  const trial = receipt.trial ?? {};
  const facts = [
    [`${trial.days ?? 15}`, 'days free'],
    [`${trial.seats ?? 30}`, 'member seats'],
    [`${trial.events ?? 20}`, 'events'],
  ];
  return (
    <div className="border border-line bg-white">
      <div className="border-b border-line px-6 py-7 text-center">
        <p className="font-market text-[11px] uppercase tracking-widest text-sea-600">
          Free trial started
        </p>
        <h1 className="mt-1 font-market text-[26px] font-bold tracking-[-0.03em] text-ink-700">
          {request.portalName || 'Your portal'} is live
        </h1>
        <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-copy-muted">
          Nothing to pay and nothing to cancel. Your trial runs until{' '}
          <strong className="text-ink-700">
            {trial.endDate ? formatLongDate(trial.endDate) : 'the end of the trial'}
          </strong>{' '}
          — upgrade any time and everything carries over.
        </p>
      </div>

      <div className="grid grid-cols-3 border-b border-line">
        {facts.map(([n, label], i) => (
          <div key={label} className={`px-4 py-4 text-center ${i > 0 ? 'border-l border-line' : ''}`}>
            <p className="font-market text-[24px] font-extrabold tabular-nums text-ink-700">{n}</p>
            <p className="font-market text-[10px] font-bold uppercase tracking-[0.16em] text-copy-label">
              {label}
            </p>
          </div>
        ))}
      </div>

      {/* They arrived ready to pay. Say what is waiting, where, and — the part
          that matters to someone handing over money — that the free days are
          added to the paid year rather than eaten by it. */}
      {receipt.plan && (
        <div className="border-b border-line bg-gold-50 px-6 py-5">
          <p className="font-market text-[10px] font-bold uppercase tracking-[0.18em] text-gold-800">
            {receipt.plan.name} — {formatMoney(receipt.plan.amount)} to pay
          </p>
          <p className="mt-2 text-sm leading-relaxed text-copy-muted">
            It is waiting on your <strong className="text-ink-700">Subscription</strong> tab with
            our bank details and QR — pay it whenever suits you and upload the screenshot. Your
            paid year is counted from the end of the free trial
            {receipt.plan.paidUntil ? (
              <>
                , running to{' '}
                <strong className="text-ink-700">{formatLongDate(receipt.plan.paidUntil)}</strong>
              </>
            ) : null}
            , so the 15 free days are yours either way.
          </p>
        </div>
      )}

      <div className="border-b border-line px-6 py-5">
        <p className="font-market text-[10px] font-bold uppercase tracking-[0.18em] text-copy-label">
          One thing left to do
        </p>
        {receipt.invited ? (
          <p className="mt-2 text-sm leading-relaxed text-copy-muted">
            We emailed <strong className="text-ink-700">{request.email}</strong> a
            link to set your password. Open it, choose a password, and you land
            straight in your portal. Messages and every other feature are
            unlimited on the trial.
          </p>
        ) : (
          <p className="mt-2 text-sm leading-relaxed text-copy-muted">
            This email already has an account, so your existing password works.
            Sign in and your new portal is waiting in the selector.
          </p>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2.5 px-6 py-5">
        <Link
          to="/signin"
          className="bg-gold-500 px-4 py-2.5 font-market text-xs font-semibold uppercase tracking-wide text-ink-800"
        >
          Go to sign in
        </Link>
        <Link
          to="/"
          className="border border-line px-4 py-2.5 font-market text-xs font-semibold uppercase tracking-wide text-ink-700 hover:border-ink-700"
        >
          Back to the home page
        </Link>
        <p className="w-full text-xs text-copy-label">
          Reference {receipt.reference} — quote it if you ever write to us.
        </p>
      </div>
    </div>
  );
}
